/**
 * Runtime registry for "content scanners" — the generalized form of the
 * old hardcoded "scan agent text, swap in a React component" pipeline that
 * previously only handled file paths.
 *
 * A scanner declares a regex and a React component. `MarkdownContent`
 * (in `@reflex/ui`) consults this registry while rendering agent text and
 * replaces matches with the scanner's component. Two targets exist:
 *
 *  - `text` scanners run over plain text nodes via a remark plugin. Use them
 *    for things `remark-gfm` will NOT autolink (absolute file paths, SHAs,
 *    issue refs).
 *  - `url` scanners are tested against the `href` of an already-linked URL.
 *    Where their component renders is the scanner's `placement`:
 *      - `inline` (the default) replaces the link in the markdown `a` slot, so
 *        the component owns how the link itself looks.
 *      - `trailing` leaves the link alone — it renders through the surface's
 *        own anchor — and renders the component once per unique matched URL
 *        after the prose, in source order. This is what link previews ride on:
 *        a card belongs under the message, not wedged between two words.
 *
 * Layering: `@reflex/ui` must never import from `web/`, so the registry lives
 * here as a browser-safe runtime singleton (same shape as the shared socket
 * manager). `web`'s plugin-registry resolves plugin manifest `componentPath`
 * strings into real components and calls {@link registerContentScanner} to
 * populate it; reflex core can register scanners directly.
 */
import { useSyncExternalStore } from 'react';
import type { ComponentType, ReactNode } from 'react';

export type ContentScannerTarget = 'text' | 'url';

/** Where a `url` scanner's component renders. `text` scanners are always inline. */
export type ContentScannerPlacement = 'inline' | 'trailing';

/**
 * Props every scanner component receives. `{ match, groups }` is the
 * contract; `children` is additive and only present for inline `url` scanners
 * (it carries the original link label so a component can render the link).
 */
export interface ContentScannerComponentProps {
  /** Full matched substring — the file path for `text`, the href for `url`. */
  match: string;
  /** Capture groups from the scanner regex (group 1 at index 0). */
  groups: string[];
  /** Link label children to render inline (inline `url` scanners only). */
  children?: ReactNode;
  /**
   * Whether the markdown this match came from is on screen. Supplied to
   * `trailing` scanners only, so a component that costs a fetch can hold off
   * until the reader is actually looking at the message. Undefined elsewhere,
   * which components should read as "no gate".
   */
  inView?: boolean;
}

export interface ContentScannerDescriptor {
  /** Stable identifier, unique across all registered scanners. */
  id: string;
  target: ContentScannerTarget;
  /** Match pattern. Normalized on registration (see below). */
  regex: RegExp;
  Component: ComponentType<ContentScannerComponentProps>;
  /** Lower sorts earlier; ties break by id. Defaults to 0. */
  order?: number;
  /** `url` scanners only; defaults to `inline`. See the module docblock. */
  placement?: ContentScannerPlacement;
}

const scanners = new Map<string, ContentScannerDescriptor>();
const listeners = new Set<() => void>();

// Cached, referentially-stable snapshot for `useSyncExternalStore`. Rebuilt
// only when the registry actually mutates so React can bail out of renders.
let snapshot: ContentScannerDescriptor[] = [];

/**
 * Normalize a scanner's regex so matching is correct and stateless:
 *  - `text` scanners are consumed via `String.matchAll`, which throws unless
 *    the regex is global — so we force the `g` flag on.
 *  - `url` scanners are consumed via `regex.test` / `regex.exec`, where a
 *    sticky/global flag would carry `lastIndex` between calls and make the
 *    result depend on call order — so we strip `g`/`y`.
 * Duplicate flags are de-duped; the source is preserved verbatim.
 */
function normalizeRegex(regex: RegExp, target: ContentScannerTarget): RegExp {
  const base = regex.flags.replace(/[gy]/g, '');
  const flags = target === 'text' ? `${base}g` : base;
  return new RegExp(regex.source, flags);
}

function rebuildSnapshot(): void {
  snapshot = [...scanners.values()].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id),
  );
}

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Register (or replace, by id) a content scanner. The regex is normalized for
 * its target; the original descriptor is otherwise stored verbatim.
 */
export function registerContentScanner(descriptor: ContentScannerDescriptor): void {
  scanners.set(descriptor.id, {
    ...descriptor,
    regex: normalizeRegex(descriptor.regex, descriptor.target),
  });
  rebuildSnapshot();
  notify();
}

/** Remove a scanner by id. No-op when the id is not registered. */
export function unregisterContentScanner(id: string): void {
  if (scanners.delete(id)) {
    rebuildSnapshot();
    notify();
  }
}

/**
 * Replace the full set of scanners contributed by a given source (e.g. all
 * plugin-manifest scanners) in one atomic update. `sourcePredicate` selects
 * the existing scanners this call owns; everything matching it is dropped and
 * the supplied `next` set takes its place. Used by the web resolver so a
 * manifest refresh cleanly reconciles add/remove without leaking stale ids.
 */
export function replaceContentScanners(
  next: ContentScannerDescriptor[],
  sourcePredicate: (id: string) => boolean,
): void {
  for (const id of [...scanners.keys()]) {
    if (sourcePredicate(id)) scanners.delete(id);
  }
  for (const descriptor of next) {
    scanners.set(descriptor.id, {
      ...descriptor,
      regex: normalizeRegex(descriptor.regex, descriptor.target),
    });
  }
  rebuildSnapshot();
  notify();
}

/** Look up a single scanner by id. */
export function getContentScannerById(id: string): ContentScannerDescriptor | undefined {
  return scanners.get(id);
}

/**
 * Current scanners, sorted by `order` then `id`. Pass a `target` to filter.
 * Non-React callers (the remark plugin, the `a`-slot dispatch) read this
 * directly at parse/render time.
 */
export function getContentScanners(target?: ContentScannerTarget): ContentScannerDescriptor[] {
  if (!target) return snapshot;
  return snapshot.filter((s) => s.target === target);
}

/** Stable full snapshot for `useSyncExternalStore`. */
function getSnapshot(): ContentScannerDescriptor[] {
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Subscribe a React component to registry changes. Returns the current
 * snapshot; the component re-renders whenever scanners are (de)registered,
 * so `MarkdownContent` picks up plugin scanners the moment manifests resolve.
 */
export function useContentScanners(): ContentScannerDescriptor[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test-only: clear every registered scanner. */
export function __resetContentScannersForTest(): void {
  scanners.clear();
  rebuildSnapshot();
  notify();
}
